import storage from 'utils/storage';
import { logger } from '@commutifi-fe/logger';

// ============================================================================
// Client Version Checker
// ============================================================================
// Detects when a new version of the app has been deployed by comparing
// the bundled version against version.json served from the server.
//
// Trigger: Tab becomes visible (user returns after being away)
//
// This avoids polling overhead while still catching stale clients reasonably fast.
// ============================================================================

/** Storage key for tracking that a new version is available */
const NEW_VERSION_AVAILABLE_KEY = 'new_version_available';

/** Storage key for cooldown to prevent rapid version checks */
const VERSION_CHECK_COOLDOWN_KEY = 'version_check_cooldown';

/**
 * Minimum time between version checks (1 minute)
 * Since the only trigger is tab visibility (user returning after being away),
 * a short cooldown is fine - they won't be rapidly switching tabs.
 */
const VERSION_CHECK_COOLDOWN_MS = 60 * 1000;

/** Storage key for reload cooldown to prevent infinite loops */
const VERSION_RELOAD_COOLDOWN_KEY = 'version_reload_cooldown';

/** Cooldown before allowing another auto-reload (30 seconds) */
const VERSION_RELOAD_COOLDOWN_MS = 30 * 1000;
type VersionInfo = {
  version: string;
  buildTime: number;
};
type VersionCheckResult = {
  hasNewVersion: boolean;
  currentVersion: string;
  serverVersion: string | null;
  error?: string;
};

/** The version this bundle was built as, resolved at build time in config/appVersion.cjs */
const CURRENT_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';

/** Flag to track if we've already detected a new version this session */
let newVersionDetected = false;

/**
 * Fetch the current version from the server
 */
async function fetchServerVersion(): Promise<VersionInfo | null> {
  try {
    const response = await fetch('/version.json', {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache'
      }
    });
    if (!response.ok) {
      return null;
    }
    const data: VersionInfo = await response.json();
    return data;
  } catch {
    return null;
  }
}

/**
 * Check if a new version is available on the server
 */
async function checkForNewVersion(): Promise<VersionCheckResult> {
  const result: VersionCheckResult = {
    hasNewVersion: false,
    currentVersion: CURRENT_VERSION,
    serverVersion: null
  };

  // Skip if we already know there's a new version
  if (newVersionDetected) {
    result.hasNewVersion = true;
    return result;
  }

  // Check cooldown
  if (storage.getWithExpiry(VERSION_CHECK_COOLDOWN_KEY)) {
    return result;
  }

  // Set cooldown for next check
  storage.putWithExpiry(VERSION_CHECK_COOLDOWN_KEY, 'true', VERSION_CHECK_COOLDOWN_MS);
  try {
    const serverVersion = await fetchServerVersion();
    if (!serverVersion) {
      result.error = 'Could not fetch server version';
      return result;
    }
    result.serverVersion = serverVersion.version;

    // Any version but this bundle's own means the server has been redeployed since it was served.
    // Deliberately an inequality and not an ordering: the version is now a `git describe` string
    // (`6.24.3-12-g0fec9d65b`), and semver reads that whole suffix as one alphanumeric prerelease
    // identifier -- so it sorts `-10-g…` *below* `-9-g…`, and every one of them below a bare
    // `6.24.3`. No ordering is needed here anyway, and a rollback should reload the client too.
    if (serverVersion.version !== CURRENT_VERSION) {
      result.hasNewVersion = true;
      newVersionDetected = true;
      storage.put(NEW_VERSION_AVAILABLE_KEY, serverVersion.version);
      logger.log('New app version detected', {
        currentVersion: CURRENT_VERSION,
        serverVersion: serverVersion.version
      });
    }
  } catch (e) {
    result.error = e instanceof Error ? e.message : 'Unknown error';
  }
  return result;
}

/**
 * Check if we should reload the page due to version mismatch.
 * Returns true if reload was triggered.
 */
export function reloadIfNewVersion(): boolean {
  if (!newVersionDetected) {
    return false;
  }

  // Check cooldown to prevent infinite reload loops
  if (storage.getWithExpiry(VERSION_RELOAD_COOLDOWN_KEY)) {
    logger.warning('Version reload blocked by cooldown');
    return false;
  }
  storage.putWithExpiry(VERSION_RELOAD_COOLDOWN_KEY, 'true', VERSION_RELOAD_COOLDOWN_MS);
  storage.remove(NEW_VERSION_AVAILABLE_KEY);
  logger.log('Reloading page to get new app version');
  window.location.reload();
  return true;
}

/**
 * Initialize version checking on visibility change.
 * Called automatically when module is imported - no manual initialization needed.
 */
function initVisibilityListener(): void {
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      checkForNewVersion();
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

// Auto-initialize when module is imported (only in browser environment)
if (typeof document !== 'undefined') {
  initVisibilityListener();
}